import '@/lib/i18n';
import { useRouter } from 'next/router';
import type { UseTranslationOptions } from 'react-i18next';
// Runtime hooks must come from next-i18next, not react-i18next: in the Next
// server bundle the two packages resolve to different module copies, so a
// react-i18next import cannot see appWithTranslation's I18nextProvider and
// silently falls back to the global English instance — every non-default
// locale page then SSRs in English and fails hydration (React #418).
import { useTranslation as useNextTranslation } from 'next-i18next';
import { useEffect } from 'react';

export type TranslationTFunction = ReturnType<typeof useNextTranslation>['t'];

export interface TranslationI18n {
  language: string;
  changeLanguage: (lng: string) => Promise<unknown>;
}

export interface TranslationHookResult {
  t: TranslationTFunction;
  i18n?: TranslationI18n;
  changeLanguage?: (lng: string) => Promise<void>;
  currentLanguage?: string;
  isRTL?: boolean;
}

export const useTranslation = (
  namespace: string = 'common',
  options?: UseTranslationOptions<string>
): TranslationHookResult => {
  const router = useRouter();
  const { t, i18n } = useNextTranslation(namespace, options);

  // Sync i18n language with Next.js locale
  useEffect(() => {
    if (router.locale && i18n.language !== router.locale) {
      i18n.changeLanguage(router.locale);
    }
  }, [router.locale, i18n]);

  return {
    t,
    i18n,
    changeLanguage: async (lng: string) => {
      // Update both i18next and Next.js router locale
      await i18n.changeLanguage(lng);
      if (router.locale !== lng && router.push) {
        await router.push(router.asPath, router.asPath, { locale: lng });
      }
    },
    currentLanguage: i18n.language || router.locale || 'en',
    isRTL: ['ar', 'he', 'fa'].includes(i18n.language),
  };
};

export default useTranslation;
